import type { ReactNode } from "react";

import { applyPlatformAwareInteractionStyles } from "../../../ui-theme";
import { mergeSxProps } from "../../lib/mergeSxProps";
import { Button, type ButtonProps } from "../Button";
import { Text } from "../Text";

export interface PickerButtonProps extends Omit<
  ButtonProps,
  "children" | "endIconType" | "variant"
> {
  primaryText: string;
  secondaryText?: ReactNode;
  centered?: boolean;
  elevated?: boolean;
}

export function PickerButton(props: PickerButtonProps) {
  const {
    primaryText,
    secondaryText,
    sx,
    centered = false,
    elevated = false,
    ...restProps
  } = props;

  return (
    <Button
      variant="outlinedHeavy"
      sx={mergeSxProps(
        (theme) => ({
          borderRadius: theme.borderRadius?.xLarge,
          backgroundColor: elevated ? "transparent" : theme.palette.background.primary,
          border: elevated ? `1px solid ${theme.palette.border?.primary}` : undefined,
          gap: 2,
          py: 3,
          paddingLeft: 3,
          paddingRight: 5,
          justifyContent: centered ? "center" : "flex-start",
          boxShadow: elevated ? theme.shadows[2] : undefined,

          ...applyPlatformAwareInteractionStyles({
            hoverStyle: {
              boxShadow: elevated
                ? `inset 0 0 0 ${theme.borderWidth?.thick} ${theme.palette.action.active}`
                : undefined,
              backgroundColor: theme.palette.background?.primary,
            },
            defaultStyle: {
              boxShadow: elevated ? `none` : undefined,
              backgroundColor: theme.palette.background.primary,
            },
          }),

          "& > :last-child": {
            marginLeft: centered ? "0" : "auto",
            marginRight: centered ? "0" : "auto",
          },

          ".MuiButton-startIcon": {
            marginRight: 0,
            borderRadius: "50%",
            backgroundColor: elevated ? "transparent" : theme.palette.background.tertiary,
            padding: 2,

            "& svg": {
              width: theme.size?.iconButton.medium.iconSize,
              height: theme.size?.iconButton.medium.iconSize,
            },
          },
        }),
        sx
      )}
      {...restProps}
    >
      <Text semibold variant="body2">
        {primaryText}
      </Text>

      {typeof secondaryText === "string" ? (
        <Text
          semibold
          variant="caption"
          sx={(theme) => ({
            marginLeft: centered ? "auto" : undefined,
            marginRight: centered ? "auto" : undefined,
            color: theme.palette.text.tertiary,
            // this is a small hack to align the text properly. Neither baseline or center alignment
            // work well here so we need to slightly push this text down to have nicer alignment
            paddingTop: "0.0625rem",
          })}
        >
          {secondaryText}
        </Text>
      ) : (
        secondaryText
      )}
    </Button>
  );
}
